import { HaulRateDto } from 'common/api/dto/rates.dto';
import TooltipTrigger from 'common/components/WithTooltip/WithTooltip';
import { WizardDateTimePicker } from 'common/components/WizardControls/WizardDateTimePicker';
import {
  WizardDropdown,
  WizardDropdownOption,
} from 'common/components/WizardControls/WizardDropdown';
import {
  TimeDuration,
  WizardDurationInput,
} from 'common/components/WizardControls/WizardDurationInput';
import { WizardNumericInput } from 'common/components/WizardControls/WizardNumericInput';
import { Contractor } from 'common/models/harvestData/contractor';
import { HaulRate } from 'common/models/harvestData/hauler';
import { roundBins } from 'common/models/harvestData/utilities';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { createContractorOptions } from 'utils/formValues/dropdownOptions';
import { DataEntryRow } from '../SchedulePickWizard/styles';
import {
  HaulingInfoLabel,
  InfoLabel,
  WizardDivider,
  WizardRow,
  WizardSection,
  selectBaseStyling,
} from '../WizardComponents/wizardStyles';
import {
  HaulRateWarning,
  PickDataValues,
  PickDataWizardContext,
} from './PickDataWizard';
import { CrewHeader, FormHeader } from './styles';

export type HarvestData = {
  /** String auto-generated by `useForm`'s `useFieldArray` hook */
  id: string;
  startTime: Date | null;
  contractor: WizardDropdownOption<Contractor> | null;
  headCount: string | null;
  timeWorked: TimeDuration;
  transferTime: TimeDuration;
  binsPicked: string | null;
  binsHauled: string | null;
  pickRate: string | null;
  tarps: string | null;
  haulRate: WizardDropdownOption<HaulRate> | null;
};

export const HarvestDataForm: FC<{
  itemIndex: number;
  itemData: HarvestData;
  removeBtn: () => JSX.Element;
}> = ({ itemIndex, itemData, removeBtn }) => {
  const { contractors, isLoading, binsEntered, setTotalBinsPicked, haulRates } =
    useContext(PickDataWizardContext);
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<PickDataValues>();

  const contractorOptions: WizardDropdownOption<Contractor>[] = useMemo(
    () => createContractorOptions(contractors),
    [contractors],
  );

  const haulRateOptions: WizardDropdownOption<HaulRateDto>[] = useMemo(
    () =>
      haulRates
        ?.filter(rate => rate.range.length > 1)
        ?.map(rate => ({
          label: `${rate.range} mi`,
          value: rate,
        })) || [],
    [haulRates],
  );

  const [selectedContractor, setSelectedContractor] = useState(
    itemData.contractor,
  );
  const [showWarning, setShowWarning] = useState(false);

  const watchForm = watch(value => {
    const record = value?.harvestDataRecords?.[itemIndex];

    if (
      itemData?.haulRate?.value.deletedAt &&
      itemData?.haulRate?.value.id !== record?.haulRate?.value?.id
    ) {
      setShowWarning(true);
    }

    setSelectedContractor(
      record?.contractor as WizardDropdownOption<Contractor>,
    );

    // Recalculate scheduled bins total
    const harvestDataRecords = value?.harvestDataRecords;
    if (harvestDataRecords) {
      let count = 0;
      harvestDataRecords.forEach(record => {
        if (record?.binsPicked) count += parseFloat(record.binsPicked);
      });

      setTotalBinsPicked(roundBins(count));
    }
  });

  const createNestedPathError = useCallback(
    (name: string) => {
      // react-hook typing does not account for nested errors
      return (
        errors.harvestDataRecords as unknown as Record<
          string,
          Record<string, string>
        >[]
      )?.[itemIndex]?.[`${name}`]?.message;
    },
    [errors.harvestDataRecords, itemIndex],
  );

  // Clean up watcher
  useEffect(() => {
    return () => watchForm.unsubscribe();
  }, [watchForm]);

  return (
    <>
      <FormHeader>
        <>
          <WizardRow>
            <CrewHeader>FLC {itemIndex + 1}:</CrewHeader>
            <TooltipTrigger
              title='contractor-name'
              tooltipText={selectedContractor?.value.contractorName || ''}
            >
              <CrewHeader>
                {selectedContractor ? selectedContractor.value.code : ''}
              </CrewHeader>
            </TooltipTrigger>
          </WizardRow>
          {removeBtn()}
        </>
      </FormHeader>
      <WizardSection>
        <DataEntryRow>
          <InfoLabel>Contractor</InfoLabel>
          <WizardDropdown<Contractor>
            name={`harvestDataRecords[${itemIndex}].contractor`}
            options={contractorOptions}
            formControl={control}
            styles={selectBaseStyling}
            isLoading={isLoading}
            nestedPathError={createNestedPathError('contractor')}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Head Count</InfoLabel>
          <WizardNumericInput
            name={`harvestDataRecords[${itemIndex}].headCount`}
            nestedPathError={createNestedPathError('headCount')}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Start Time</InfoLabel>
          <WizardDateTimePicker
            name={`harvestDataRecords[${itemIndex}].startTime`}
            formControl={control}
            showTime
            nestedPathError={createNestedPathError('startTime')}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Hours Worked</InfoLabel>
          <WizardDurationInput
            name={`harvestDataRecords[${itemIndex}].timeWorked`}
            formControl={control}
            nestedPathError={createNestedPathError('timeWorked')}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Transfer Time</InfoLabel>
          <WizardDurationInput
            name={`harvestDataRecords[${itemIndex}].transferTime`}
            formControl={control}
            nestedPathError={createNestedPathError('transferTime')}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Pick Rate</InfoLabel>
          <WizardNumericInput
            name={`harvestDataRecords[${itemIndex}].pickRate`}
            nestedPathError={createNestedPathError('pickRate')}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Bins Picked</InfoLabel>
          <WizardNumericInput
            name={`harvestDataRecords[${itemIndex}].binsPicked`}
            nestedPathError={createNestedPathError('binsPicked')}
          />
        </DataEntryRow>
        <DataEntryRow>
          <WizardDivider />
          <HaulingInfoLabel>Hauling</HaulingInfoLabel>
          <WizardDivider />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Bins Hauled</InfoLabel>
          <WizardNumericInput
            name={`harvestDataRecords[${itemIndex}].binsHauled`}
            nestedPathError={createNestedPathError('binsHauled')}
            placeholder='-'
            isDisabled={binsEntered}
          />
        </DataEntryRow>
        <DataEntryRow>
          <InfoLabel>Tarps</InfoLabel>
          <WizardNumericInput
            name={`harvestDataRecords[${itemIndex}].tarps`}
            nestedPathError={createNestedPathError('tarps')}
            placeholder='-'
            isDisabled={binsEntered}
          />
        </DataEntryRow>
        {showWarning && <DataEntryRow>{HaulRateWarning}</DataEntryRow>}
        <DataEntryRow>
          <InfoLabel>Haul Rate</InfoLabel>
          <WizardDropdown<HaulRateDto>
            name={`harvestDataRecords[${itemIndex}].haulRate`}
            options={haulRateOptions}
            formControl={control}
            styles={selectBaseStyling}
            isLoading={isLoading}
            nestedPathError={createNestedPathError('haulRate')}
            allowDeselect
            isDisabled={binsEntered}
          />
        </DataEntryRow>
      </WizardSection>
    </>
  );
};
